import type { OnChangeFn, RowSelectionState } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { getRowIds } from '../../_shared/getSelectedRowIds.js';

type UseRowSelectionProps = {
    disableAutoRowSelection?: boolean;
    onRowSelectionChange?: (updateValues: string[], currentValues: string[]) => void;
    setRowSelectionHighlights?: (updateValues: string[]) => string[];
};

export function useRowSelection({
    disableAutoRowSelection,
    setRowSelectionHighlights,
    onRowSelectionChange,
}: UseRowSelectionProps) {
    const [rowSelection, setRowSelection] = useState<RowSelectionState>({});

    const rowSelectionHighlights = useMemo(() => {
        return setRowSelectionHighlights?.(getRowIds(rowSelection));
    }, [rowSelection, setRowSelectionHighlights]);

    const updateRowSelection: OnChangeFn<RowSelectionState> = useCallback(
        (updateFnOrValue) => {
            // get the updated value
            const updatedValue =
                typeof updateFnOrValue === 'function'
                    ? // function
                      updateFnOrValue(rowSelection)
                    : // value
                      updateFnOrValue;

            // auto update internal state if configured
            if (!disableAutoRowSelection) {
                setRowSelection(updatedValue);
            }

            // run the on selection change callback
            onRowSelectionChange?.(getRowIds(updatedValue), getRowIds(rowSelection));
        },
        [disableAutoRowSelection, onRowSelectionChange, rowSelection]
    );

    return {
        rowSelectionHighlights,
        rowSelection,
        setRowSelection,
        updateRowSelection,
    };
}
